import {
  FIREBASE_TO_ZOHO_ACCOUNT_SYNC_ENDPOINT,
  FIREBASE_TO_ZOHO_PRODUCT_SYNC_ENDPOINT,
  ZOHO_TO_FIREBASE_ACCOUNT_SYNC_ENDPOINT,
  ZOHO_TO_FIREBASE_PRODUCT_SYNC_ENDPOINT,
  ZOHO_TO_FIREBASE_WEBSITE_ACCOUNT_SYNC_ENDPOINT
} from 'common/constants'
import {Collection} from 'common/enums'
import {FirebaseInstances, handleAPIRequest} from './helpers'
import {collectionsApi} from 'lib/api'

type QueryOperator =
  | '=='
  | '!='
  | '<'
  | '<='
  | '>'
  | '>='
  | 'array-contains'
  | 'in'
  | 'array-contains-any'
  | 'not-in'

export function createQueryService(fb: FirebaseInstances) {
  const handleSyncFirebaseToZohoAccountAPI = async () => {
    return handleAPIRequest(FIREBASE_TO_ZOHO_ACCOUNT_SYNC_ENDPOINT)
  }

  const handleSyncZohoToFirebaseProductAPI = async () => {
    return handleAPIRequest(ZOHO_TO_FIREBASE_PRODUCT_SYNC_ENDPOINT)
  }

  const handleSyncZohoToFirebaseAccountAPI = async () => {
    return handleAPIRequest(ZOHO_TO_FIREBASE_ACCOUNT_SYNC_ENDPOINT)
  }

  const handleSyncFirebaseToZohoProductAPI = async () => {
    return handleAPIRequest(FIREBASE_TO_ZOHO_PRODUCT_SYNC_ENDPOINT)
  }

  const handleSyncZohoToFirebaseWebsiteAccountAPI = async () => {
    return handleAPIRequest(ZOHO_TO_FIREBASE_WEBSITE_ACCOUNT_SYNC_ENDPOINT)
  }

  const getCollectionBasedOnQuery = async <T>(
    collectionName: string,
    queryVariable: string,
    queryValue: string | boolean,
    operator: QueryOperator = '=='
  ) => {
    const uid = fb.auth?.currentUser?.uid
    if (!uid) return {}
    try {
      const params: Record<string, string> = {
        [queryVariable]: String(queryValue),
        operator: operator,
        orderBy: 'lastUpdated',
        orderDir: 'desc'
      }
      const result = await collectionsApi.query<{data: T[]}>(collectionName, params)
      const data = (result as any)?.data || result
      return {data: Array.isArray(data) ? data as T[] : []}
    } catch (error) {
      console.error(error)
      return {error}
    }
  }

  const getCollectionSnapshotBasedOnQuery = <T>({
    updateState, cancelCallback, collectionName, queryVariable, queryValue
  }: {
    updateState: React.Dispatch<React.SetStateAction<{documentData: T[]} | undefined>>
    cancelCallback: (error: Error) => void
    collectionName: string; queryVariable: string; queryValue: string | boolean
  }) => {
    try {
      const uid = fb.auth?.currentUser?.uid
      if (!uid) throw new Error('user not logged in.', {cause: {message: 'user not logged in.', name: 'USER_NOT_FOUND'}})

      const fetchData = async () => {
        try {
          const params: Record<string, string> = {
            [queryVariable]: String(queryValue),
            orderBy: 'lastUpdated', orderDir: 'desc'
          }
          const result = await collectionsApi.query<{data: T[]}>(collectionName, params)
          const data = (result as any)?.data || result
          updateState({documentData: Array.isArray(data) ? data : []})
        } catch (e) {
          cancelCallback(e as Error)
        }
      }
      fetchData()
      return {unsub: () => {}}
    } catch (error) {
      return {error}
    }
  }

  const getCollectionBasedOnIds = async ({
    collectionName, queryVariable, idsArray, arrayCondition = 'in'
  }: {
    collectionName: string; queryVariable: string
    arrayCondition?: 'in' | 'not-in'; idsArray: Array<string>
  }) => {
    const uid = fb.auth?.currentUser?.uid
    if (!uid) return
    try {
      const result = await collectionsApi.queryByIds<{data: any[]}>(
        collectionName, queryVariable, idsArray
      )
      return {collectionData: (result as any)?.data || result || []}
    } catch (error) {
      return {error}
    }
  }

  const getCollectionData = async (collectionName: string) => {
    const uid = fb.auth?.currentUser?.uid
    if (!uid) return {}
    try {
      const result = await collectionsApi.query<{data: any[]}>(
        collectionName, {orderBy: 'lastUpdated', orderDir: 'desc'}
      )
      return {data: (result as any)?.data || result || []}
    } catch (error) {
      console.error(error)
      return {error}
    }
  }

  const checkDocuemnt = async ({
    documentId, collectionName, queryVariable, queryValue
  }: {
    documentId: string; collectionName: string
    queryVariable: string; queryValue: string | number | boolean
  }) => {
    try {
      const result = await collectionsApi.getDocument<{data: any}>(collectionName, documentId)
      const data = (result as any)?.data || result
      if (data && data[queryVariable] === queryValue) return {exists: true}
      return {exists: false}
    } catch (error) {
      return {error}
    }
  }

  const getDocumentData = (
    updateState: React.Dispatch<React.SetStateAction<{documentData: any}>>,
    cancelCallback: (error: Error) => void,
    documentId: string,
    collectionName: string
  ) => {
    try {
      const uid = fb.auth?.currentUser?.uid
      if (!uid) throw new Error('user not logged in.', {cause: {message: 'user not logged in.', name: 'USER_NOT_FOUND '}})

      const fetchDoc = async () => {
        try {
          const result = await collectionsApi.getDocument<{data: any}>(
            collectionName || Collection.Contacts, documentId || uid
          )
          const data = (result as any)?.data || result || {}
          updateState({documentData: {...data, documentId: documentId || uid}})
        } catch (e) {
          cancelCallback(e as Error)
        }
      }
      fetchDoc()
      return {unsSubData: () => {}}
    } catch (error) {
      return {error}
    }
  }

  const getSingleDocumentData = async (docId: string, colelctionName: string) => {
    try {
      const uid = fb.auth?.currentUser?.uid
      if (!uid) throw new Error('user not logged in.', {cause: {message: 'user not logged in.', name: 'USER_NOT_FOUND '}})

      const result = await collectionsApi.getDocument<{data: any}>(colelctionName, docId)
      return {data: (result as any)?.data || result}
    } catch (error) {
      return {error}
    }
  }

  return {
    handleAPIRequest: handleAPIRequest,
    handleSyncFirebaseToZohoAccountAPI,
    handleSyncZohoToFirebaseProductAPI,
    handleSyncZohoToFirebaseAccountAPI,
    handleSyncFirebaseToZohoProductAPI,
    handleSyncZohoToFirebaseWebsiteAccountAPI,
    getCollectionBasedOnQuery, getCollectionSnapshotBasedOnQuery,
    getCollectionBasedOnIds, getCollectionData, checkDocuemnt,
    getDocumentData, getSingleDocumentData
  }
}
